import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Loader2, X, Link2 , Star } from "lucide-react";
import { toast } from "sonner";

interface Author { id: string; name: string; }

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  keywords: string[];
  primaryKeyword?: string;
  onPrimaryChange?: (kw: string) => void;
  onRemoveKeyword: (k: string) => void;
  onGenerated: (articleId: string) => void;
  defaultInternalUrl?: string;
  targetCountry?: string | null;
}

const GenerateArticleDialog = ({ open, onOpenChange, keywords, primaryKeyword, onPrimaryChange, onRemoveKeyword, onGenerated, defaultInternalUrl, targetCountry }: Props) => {
  const [authors, setAuthors] = useState<Author[]>([]);
  const [imageCount, setImageCount] = useState<string>("4");
  const [authorId, setAuthorId] = useState<string>("");
  const [internalUrl, setInternalUrl] = useState(defaultInternalUrl || "https://maroctransfers.com/");
  const [loading, setLoading] = useState(false);
  const [localPrimary, setLocalPrimary] = useState<string>("");
  const primary = (primaryKeyword && keywords.includes(primaryKeyword)) ? primaryKeyword
    : (localPrimary && keywords.includes(localPrimary)) ? localPrimary : keywords[0];
  const setPrimary = (k: string) => { setLocalPrimary(k); onPrimaryChange?.(k); };

  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data } = await (supabase as any).from("blog_authors").select("id,name").order("name");
      setAuthors(data || []);
      if (data && data.length && !authorId) setAuthorId(data[0].id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const launch = async () => {
    if (keywords.length === 0) { toast.error("Ajoutez au moins un mot-clé"); return; }
    if (!authorId) { toast.error("Sélectionnez un auteur"); return; }
    if (!/^https?:\/\//i.test(internalUrl)) { toast.error("URL de backlink invalide"); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("seo-generate-article", {
        body: {
          keywords: primary ? [primary, ...keywords.filter((k) => k !== primary)] : keywords,
          internalUrl,
          image_count: parseInt(imageCount, 10),
          author_id: authorId,
          target_country: targetCountry || null,
        },
      });
      if (error) throw error;
      const article = (data as any)?.article;
      if (!article?.id) throw new Error("Pas d'article généré");
      toast.success("Brouillon créé — direction l'audit");
      onOpenChange(false);
      onGenerated(article.id);
    } catch (e: any) {
      toast.error(e?.message || "Échec de la génération");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" /> Configuration de la génération IA
          </DialogTitle>
          <DialogDescription>
            Personnalisez le brouillon avant que l'IA ne rédige l'article.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <p className="text-xs font-medium mb-2">Mots-clés ({keywords.length}) — cliquez l'étoile pour choisir le mot-clé principal</p>
            <div className="flex flex-wrap gap-2">
              {keywords.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">Aucun mot-clé.</p>
              ) : keywords.map((k) => (
                <Badge key={k} variant={k === primary ? "default" : "secondary"} className={`px-3 py-1.5 text-sm ${k === primary ? "bg-gradient-gold text-primary-foreground" : ""}`}>
                  <button type="button" title="Définir comme mot-clé principal" onClick={() => setPrimary(k)} className="mr-1.5">
                    <Star className={`h-3.5 w-3.5 ${k === primary ? "fill-current" : "opacity-50"}`} />
                  </button>
                  {k}{k === primary && <span className="ml-1.5 text-[10px] uppercase font-semibold">Principal</span>}
                  <button onClick={() => onRemoveKeyword(k)} className="ml-2 hover:text-destructive"><X className="h-3 w-3" /></button>
                </Badge>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">Nombre de photos à insérer</label>
            <Select value={imageCount} onValueChange={setImageCount}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="2">2 photos</SelectItem>
                <SelectItem value="3">3 photos</SelectItem>
                <SelectItem value="4">4 photos</SelectItem>
                <SelectItem value="5">5 photos</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">1 image héro + le reste réparti dans le corps de l'article.</p>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">Auteur de l'article</label>
            <Select value={authorId} onValueChange={setAuthorId}>
              <SelectTrigger><SelectValue placeholder="Sélectionnez un auteur" /></SelectTrigger>
              <SelectContent>
                {authors.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 flex items-center gap-1"><Link2 className="h-3.5 w-3.5 text-primary" /> URL de maillage interne</label>
            <Input value={internalUrl} onChange={(e) => setInternalUrl(e.target.value)} />
            <p className="text-xs text-muted-foreground mt-2">L'encadré « L'erreur à éviter » est désormais généré automatiquement par l'IA à partir du sujet de l'article (1-2 phrases, courtes et concrètes).</p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Annuler</Button>
          <Button onClick={launch} disabled={loading} className="bg-gradient-gold text-primary-foreground">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
            {loading ? "Génération en cours…" : "Lancer la génération"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GenerateArticleDialog;